/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DrmType, PlayerState, PlayerViewMode } from "../enums";
import type {
  IPlayer,
  IPlayerView,
  IStandaloneMediaPlayer,
  IVideoManager,
  IVpcDrmInfo
} from "../interfaces";
import { StandaloneMediaPlayerImpl } from "../impls/StandaloneMediaPlayerImpl";
import type { ISubscription } from "../ISubscription";
import { SubscriptionModel } from "../SubscriptionModel";
import { CryptoProviderFactory } from "../../device/crypto";
import { Log, Diagnostics } from "../../logging";
import { DimensionScaler } from "../../utility/DimensionScaler";
import { TrickPlayManager } from "../trickplay";
import type { ITrickPlayConfig, ITrickPlayEligibility } from "../trickplay";
import type {
  TActiveDrmInfo,
  TAudioTrack,
  TFirstFrameEvent,
  TPigConfig,
  TPlaybackConfig,
  TPlayerDrmConfig,
  TPlayerError,
  TPlayerEventMap,
  TPlayerSourceConfig,
  TPlayerStats,
  TSeekRange,
  TStandalonePlaybackEvent,
  TStateChangedEvent,
  TTextTrack,
  TVariantTrack
} from "../types";

/**
 * Wraps ShakaVideoPlayerImpl via IPlayer interface. Subscribes to player
 * events via IPlayer.on() and exposes ISubscription<PlayerState> and
 * ISubscription<PlayerViewMode> for state/view change notifications.
 */
export class VideoManager implements IVideoManager, IPlayerView {
  private static readonly TAG: string = "VideoManager";
  private static readonly CSS_FULL_SCREEN: string = "full-screen";
  private static readonly CSS_PIP: string = "pip";
  private static readonly CSS_PIG: string = "pig";
  private static readonly CSS_ATMOSPHERIC: string = "atmospheric";
  private static readonly CSS_BROWSER_FULLSCREEN: string = "browser-fullscreen";
  private static readonly CSS_HIDDEN: string = "hidden";
  private static readonly CSS_SURFACE_BLANKED: string = "surface-blanked";

  private static readonly HEADER_PLAYBACK_SESSION_ID: string =
    "x-playback-session-id";
  private static readonly HEADER_CLIENT_REQUEST_ID: string =
    "x-mobitv-client-request-id";
  private static readonly HEADER_DEVICE_USER_AGENT: string =
    "x-device-user-agent";
  private static readonly CLIENT_REQUEST_ID_SEED_RANGE: number = 100;

  private mPlayer: IPlayer;
  private mContainerEl: HTMLDivElement | null = null;
  private mPlayerStateSubscription: SubscriptionModel<PlayerState> =
    new SubscriptionModel<PlayerState>();
  private mPlayerViewSubscription: SubscriptionModel<PlayerViewMode> =
    new SubscriptionModel<PlayerViewMode>();
  private mPlayerErrorSubscription: SubscriptionModel<TPlayerError> =
    new SubscriptionModel<TPlayerError>();
  private mFirstFrameSubscription: SubscriptionModel<TFirstFrameEvent> =
    new SubscriptionModel<TFirstFrameEvent>();
  private mInitiationSubscription: SubscriptionModel<void> =
    new SubscriptionModel<void>();
  private mDrmConfiguredSubscription: SubscriptionModel<void> =
    new SubscriptionModel<void>();
  private mDrmFailedSubscription: SubscriptionModel<void> =
    new SubscriptionModel<void>();
  /**
   * Glue player for standalone media (an EAS alert tone and similar
   * progressive files), playing on its own element outside the Shaka
   * pipeline. Kept entirely separate from mPlayer so the two can be started
   * and stopped independently.
   */
  private mStandalonePlayer: IStandaloneMediaPlayer;
  private mStandalonePlaybackEventSubscription: SubscriptionModel<TStandalonePlaybackEvent> =
    new SubscriptionModel<TStandalonePlaybackEvent>();
  private mStandaloneEventHandler: (event: TStandalonePlaybackEvent) => void;

  private mDrmInfo: IVpcDrmInfo | null = null;
  private mCurrentViewMode: PlayerViewMode = PlayerViewMode.FullScreen;
  private mStreamingUrl: string | null = null;

  /**
   * Advanced by every playUri() and every teardown, so a load that completes
   * after being replaced does not start the player.
   */
  private mPlayUriSequence: number = 0;
  private mPlaybackSessionId: string = "";
  private mClientRequestId: number = 0;
  /**
   * Mirrors the player's text-track visibility. The player starts with text
   * hidden after load, so this is false until captions are explicitly shown.
   */
  private mIsTextTrackVisible: boolean = false;

  /** TrickPlayManager instance, null until initTrickPlay() is called. */
  private mTrickPlayManager: TrickPlayManager | null = null;

  private mStateChangedHandler: (
    event: TPlayerEventMap[PlayerState.STATE_CHANGED]
  ) => void;
  private mErrorHandler: (event: TPlayerEventMap[PlayerState.ERROR]) => void;
  private mFirstFrameHandler: (
    event: TPlayerEventMap[PlayerState.FIRST_FRAME]
  ) => void;
  private mInitiationHandler: (
    event: TPlayerEventMap[PlayerState.INITIATION]
  ) => void;
  private mDrmConfiguredHandler: (
    event: TPlayerEventMap[PlayerState.DRM_CONFIGURED]
  ) => void;
  private mDrmFailedHandler: (
    event: TPlayerEventMap[PlayerState.DRM_FAILED]
  ) => void;

  constructor(
    player: IPlayer,
    standalonePlayer: IStandaloneMediaPlayer = new StandaloneMediaPlayerImpl()
  ) {
    this.mPlayer = player;
    this.mStandalonePlayer = standalonePlayer;
    this.mStandaloneEventHandler = this.onStandalonePlaybackEvent.bind(this);
    this.mStateChangedHandler = this.onStateChanged.bind(this);
    this.mErrorHandler = this.onError.bind(this);
    this.mFirstFrameHandler = this.onFirstFrame.bind(this);
    this.mInitiationHandler = this.onInitiation.bind(this);
    this.mDrmConfiguredHandler = this.onDrmConfigured.bind(this);
    this.mDrmFailedHandler = this.onDrmFailed.bind(this);
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](VideoManager.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      VideoManager.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(VideoManager.TAG, "stacktrace:,", optionalParams[0]);
    }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  public init(
    videoEl: HTMLVideoElement,
    containerEl: HTMLDivElement,
    standaloneEl?: HTMLVideoElement
  ): void {
    this.log("debug", "init()");
    this.mContainerEl = containerEl;
    this.mPlaybackSessionId =
      CryptoProviderFactory.getProvider().generateUuidV4();
    this.mClientRequestId = Math.floor(
      // eslint-disable-next-line sonarjs/pseudo-random
      Math.random() * VideoManager.CLIENT_REQUEST_ID_SEED_RANGE
    );
    this.mPlayer.init(videoEl, containerEl);
    this.mPlayer.on(PlayerState.STATE_CHANGED, this.mStateChangedHandler);
    this.mPlayer.on(PlayerState.ERROR, this.mErrorHandler);
    this.mPlayer.on(PlayerState.FIRST_FRAME, this.mFirstFrameHandler);
    this.mPlayer.on(PlayerState.INITIATION, this.mInitiationHandler);
    this.mPlayer.on(PlayerState.DRM_CONFIGURED, this.mDrmConfiguredHandler);
    this.mPlayer.on(PlayerState.DRM_FAILED, this.mDrmFailedHandler);

    // Standalone playback is optional: callers that never play an alert tone
    // (and the existing tests) pass no element and get no standalone player.
    if (standaloneEl !== undefined) {
      this.mStandalonePlayer.init(standaloneEl);
      this.mStandalonePlayer
        .getPlaybackEventSubscription()
        .subscribe(this.mStandaloneEventHandler);
    }
  }

  public async load(config: TPlayerSourceConfig): Promise<void> {
    this.log("debug", "load()");
    await this.mPlayer.load(config);
    this.mStreamingUrl = config.url;
  }

  public stop(): void {
    this.log("debug", "stop()");
    this.mPlayUriSequence += 1;
    if (this.mTrickPlayManager !== null) {
      this.mTrickPlayManager.stop();
      this.mTrickPlayManager = null;
    }
    this.mStreamingUrl = null;
    this.mIsTextTrackVisible = false;
    this.mPlayer.unload().catch((error: unknown) => {
      this.log("error", "stop()", error);
    });
  }

  public clean(): void {
    this.log("debug", "clean()");
    this.mPlayUriSequence += 1;
    if (this.mTrickPlayManager !== null) {
      this.mTrickPlayManager.stop();
      this.mTrickPlayManager = null;
    }
    this.mStreamingUrl = null;
    this.mIsTextTrackVisible = false;
    this.mPlayer.unload().catch((error: unknown) => {
      this.log("error", "clean()", error);
    });
  }

  public async unload(): Promise<void> {
    this.log("debug", "unload()");
    this.mPlayUriSequence += 1;
    this.mStreamingUrl = null;
    this.mIsTextTrackVisible = false;
    await this.mPlayer.unload();
  }

  // ── Standalone Playback ────────────────────────────────────────────────────
  //
  // Standalone playback runs on its own glue player and its own media element,
  // and is deliberately absent from stop(), clean() and unload() above: those
  // tear down main playback, which happens on every tune, and an alert tone
  // must survive that. The two are stopped independently — stopping one never
  // stops the other.

  public startStandalonePlayback(url: string): void {
    this.log("info", "startStandalonePlayback()");
    this.mStandalonePlayer.startPlayback(url);
  }

  public stopStandalonePlayback(): void {
    this.log("info", "stopStandalonePlayback()");
    this.mStandalonePlayer.stopPlayback();
  }

  public isStandalonePlaying(): boolean {
    return this.mStandalonePlayer.isPlaying();
  }

  public getStandalonePlaybackEventSubscription(): ISubscription<TStandalonePlaybackEvent> {
    return this.mStandalonePlaybackEventSubscription;
  }

  /**
   * Republish a standalone playback event (PLAYING, end of media, ERROR) to
   * this manager's subscribers.
   *
   * @param event - The event raised by the standalone glue player
   */
  private onStandalonePlaybackEvent(event: TStandalonePlaybackEvent): void {
    this.mStandalonePlaybackEventSubscription.notify(event);
  }

  public async destroy(): Promise<void> {
    this.log("debug", "destroy()");
    this.mPlayer.off(PlayerState.STATE_CHANGED, this.mStateChangedHandler);
    this.mPlayer.off(PlayerState.ERROR, this.mErrorHandler);
    this.mPlayer.off(PlayerState.FIRST_FRAME, this.mFirstFrameHandler);
    this.mPlayer.off(PlayerState.INITIATION, this.mInitiationHandler);
    this.mPlayer.off(PlayerState.DRM_CONFIGURED, this.mDrmConfiguredHandler);
    this.mPlayer.off(PlayerState.DRM_FAILED, this.mDrmFailedHandler);
    this.mStandalonePlayer
      .getPlaybackEventSubscription()
      .unsubscribe(this.mStandaloneEventHandler);
    this.mStandalonePlayer.destroy();
    await this.mPlayer.destroy();
    this.mContainerEl = null;
    this.mDrmInfo = null;
    this.mStreamingUrl = null;
    this.mTrickPlayManager = null;
  }

  // ── Playback Control ───────────────────────────────────────────────────────

  public pause(): void {
    this.log("debug", "pause()");
    this.mPlayer.pause();
  }

  public resume(): void {
    this.log("debug", "resume()");
    this.mPlayer.play();
  }

  /**
   * Loads and plays `url`.
   *
   * @param startPosition - Seconds to start from, applied by the player as
   *   part of the load. VOD resume relies on this: the saved position is
   *   handed over here rather than sought to once the stream has loaded.
   */
  public playUri(url: string, startPosition?: number): void {
    this.log("debug", "playUri()");
    this.mPlayUriSequence += 1;
    const playUriSequence: number = this.mPlayUriSequence;
    this.mStreamingUrl = url;
    const playerBackConfig: TPlaybackConfig = {
      autoplay: true,
      muted: false,
      startPosition: startPosition,
      playbackUrlRequestHeaders: this.buildPlaybackUrlRequestHeaders()
    };
    const config: TPlayerSourceConfig = {
      url,
      playbackConfig: playerBackConfig
    };

    if (this.mDrmInfo !== null && this.mDrmInfo !== undefined) {
      const drmConfig = this.buildDrmConfig(this.mDrmInfo);
      // Only attach a configuration that names a DRM system. Providers that
      // deliver in the clear — TiVo+ among them — resolve to no DRM type at
      // all, and handing the player an empty configuration for an
      // unencrypted stream asks it to set up protection there is none of.
      if (drmConfig.drmType !== undefined && drmConfig.drmType.length > 0) {
        config.drmConfig = drmConfig;
      } else {
        this.log("debug", "playUri() - clear stream, no DRM configured");
      }
    }
    this.mPlayer
      .load(config)
      .then(() => {
        // A newer playUri() or a stop owns the player now; starting it here
        // would play their source, or nothing at all.
        if (playUriSequence !== this.mPlayUriSequence) {
          return;
        }
        this.log("debug", "playUri() - playback started");
        this.mPlayer.play();
      })
      .catch((error: unknown) => {
        this.log("error", "playUri()", error);
      });
  }

  public restartPlaybackWithPosition(position: number): void {
    this.log("debug", "restartPlaybackWithPosition()");
    this.mPlayer.seek(position);
    this.mPlayer.play();
  }

  public setPlayPosition(position: number): void {
    this.log("debug", "setPlayPosition()");
    this.mPlayer.seek(position);
  }

  // ── Trick-Play ─────────────────────────────────────────────────────────────

  /**
   * Initialize the TrickPlayManager with the given config and eligibility.
   * Call after playback is loaded and capability information is available.
   *
   * @param config - Trick-play configuration values.
   * @param eligibility - Platform x channel eligibility rules.
   */
  public initTrickPlay(
    config: ITrickPlayConfig,
    eligibility: ITrickPlayEligibility
  ): void {
    this.mTrickPlayManager = new TrickPlayManager(
      this.mPlayer,
      config,
      eligibility
    );
    this.mTrickPlayManager.init();
    this.log("debug", "initTrickPlay()");
  }

  /**
   * Get the TrickPlayManager instance.
   * @returns The TrickPlayManager, or null if not initialized.
   */
  public getTrickPlayManager(): TrickPlayManager | null {
    return this.mTrickPlayManager;
  }

  // ── Availability Window ────────────────────────────────────────────────────

  public overrideAvailabilityWindowForLinear(): void {
    this.log("debug", "overrideAvailabilityWindowForLinear()");
    this.mPlayer.overrideAvailabilityWindowForLinear();
  }

  public overrideAvailabilityWindowForStartOver(): void {
    this.log("debug", "overrideAvailabilityWindowForStartOver()");
    this.mPlayer.overrideAvailabilityWindowForStartOver();
  }

  // ── Audio Tracks ───────────────────────────────────────────────────────────

  public getAudioTracks(): TAudioTrack[] {
    return this.mPlayer.getAudioTracks();
  }

  public getSelectedAudioTrack(): TAudioTrack | null {
    const tracks = this.mPlayer.getAudioTracks();
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].active) {
        return tracks[i];
      }
    }
    return null;
  }

  public setAudioTrack(audioId: number): void {
    const tracks = this.mPlayer.getAudioTracks();
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].id === audioId) {
        this.mPlayer.setAudioTrack(tracks[i]);
        return;
      }
    }
    this.log("warn", "setAudioTrack() - track not found for id: " + audioId);
  }

  public setAudioTrackByLanguage(language: string): void {
    this.mPlayer.setAudioTrackByLanguage(language);
  }

  public mute(isMute: boolean): void {
    this.mPlayer.setMuted(isMute);
  }

  public isMuted(): boolean {
    return this.mPlayer.isMuted();
  }

  // ── Text Tracks ────────────────────────────────────────────────────────────

  public getTextTracks(): TTextTrack[] {
    return this.mPlayer.getTextTracks();
  }

  public getSelectedTextTrack(): TTextTrack | null {
    const tracks = this.mPlayer.getTextTracks();
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].active) {
        return tracks[i];
      }
    }
    return null;
  }

  public setTextTrack(trackId: number): void {
    const tracks = this.mPlayer.getTextTracks();
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].id === trackId) {
        this.mPlayer.setTextTrack(tracks[i]);
        this.mPlayer.setTextTrackVisibility(true);
        this.mIsTextTrackVisible = true;
        return;
      }
    }
    this.log("warn", "setTextTrack() - track not found for id: " + trackId);
  }

  public isTextTrackSupported(): boolean {
    return this.mPlayer.isTextTrackSupported();
  }

  public isTextTrackChangeSupported(): boolean {
    return this.mPlayer.getTextTracks().length > 1;
  }

  public setTextTrackVisibility(visible: boolean): void {
    this.mPlayer.setTextTrackVisibility(visible);
    this.mIsTextTrackVisible = visible;
  }

  public isClosedCaptionActive(): boolean {
    return this.getSelectedTextTrack() !== null && this.mIsTextTrackVisible;
  }

  // ── Player Info ────────────────────────────────────────────────────────────

  public getDuration(): number {
    return this.mPlayer.getDuration();
  }

  public getPlayerCurrentTime(): number {
    return this.mPlayer.getCurrentTime();
  }

  public getSeekableRange(): TSeekRange {
    return this.mPlayer.getSeekableRange();
  }

  public getStats(): TPlayerStats {
    return this.mPlayer.getStats();
  }

  public getStreamingUrl(): string | null {
    return this.mStreamingUrl;
  }

  public isDynamicManifest(): boolean {
    return this.mPlayer.isLive();
  }

  public getBufferedInfo(): TimeRanges | null {
    try {
      return this.mPlayer.getBufferedRanges();
    } catch (error: unknown) {
      this.log("error", "getBufferedInfo()", error);
      return null;
    }
  }

  public getBufferAheadSeconds(): number {
    try {
      return this.mPlayer.getBufferAheadSeconds();
    } catch (error: unknown) {
      this.log("error", "getBufferAheadSeconds()", error);
      return 0;
    }
  }

  public getAssetUri(): string | null {
    return this.mPlayer.getAssetUri();
  }

  public getVideoVariants(): TVariantTrack[] {
    return this.mPlayer.getVariantTracks();
  }

  public getAudioVideoCodec(): string | null {
    const variants = this.mPlayer.getVariantTracks();
    for (let i = 0; i < variants.length; i++) {
      if (variants[i].active) {
        return variants[i].codecs;
      }
    }
    return null;
  }

  // ── DRM ────────────────────────────────────────────────────────────────────

  public getDrmInfo(): IVpcDrmInfo | null {
    return this.mDrmInfo;
  }

  public getDrmServerType(): DrmType | null {
    if (this.mDrmInfo === null) {
      return null;
    }
    return this.mDrmInfo.drmType;
  }

  public getActiveDrmInfo(): TActiveDrmInfo | null {
    return this.mPlayer.getActiveDrmInfo();
  }

  public getKeyStatuses(): Record<string, string> {
    return this.mPlayer.getKeyStatuses();
  }

  public getTransportEncodingType(): string | null {
    if (this.mDrmInfo === null) {
      return null;
    }
    const encoding = this.mDrmInfo.transportEncodingType;
    if (encoding === null || encoding === undefined) {
      return null;
    }
    return encoding;
  }

  public setupDRM(drmInfo: IVpcDrmInfo): void {
    this.log("debug", "setupDRM()");
    this.mDrmInfo = drmInfo;
  }

  /**
   * Forgets the DRM configuration, so the next load is a clear one.
   *
   * playUri() attaches whatever configuration was last set up, and that
   * survives stop() and clean() — a session-less VOD preview, which is played
   * in the clear, would otherwise inherit the previous title's Widevine setup
   * and ask the player to protect a stream that is not encrypted.
   */
  public clearDrm(): void {
    this.log("debug", "clearDrm()");
    this.mDrmInfo = null;
  }

  public updateDRMInfo(drmInfo: IVpcDrmInfo): void {
    this.log("debug", "updateDRMInfo()");
    this.mDrmInfo = drmInfo;
    const drmConfig = this.buildDrmConfig(drmInfo);
    this.mPlayer.updateDrmToken(drmConfig);
  }

  // ── View Lifecycle (IPlayerView) ───────────────────────────────────────────

  public mount(): void {
    this.log("debug", "mount()");
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.mContainerEl.classList.remove(VideoManager.CSS_HIDDEN);
    }
  }

  public unmount(): void {
    this.log("debug", "unmount()");
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.mContainerEl.classList.add(VideoManager.CSS_HIDDEN);
    }
  }

  /**
   * Shows the container's black background in place of the video picture,
   * without unloading the stream. Independent of the view mode, so it holds
   * across fullScreen()/pig()/atmospheric() and the rest.
   *
   * @param isBlanked - True to blank the picture, false to show it again.
   */
  public setSurfaceBlanked(isBlanked: boolean): void {
    this.log("debug", "setSurfaceBlanked() - " + String(isBlanked));
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.mContainerEl.classList.toggle(
        VideoManager.CSS_SURFACE_BLANKED,
        isBlanked
      );
    }
  }

  public fullScreen(): void {
    this.log("debug", "fullScreen()");
    this.mCurrentViewMode = PlayerViewMode.FullScreen;
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.clearViewModeClasses();
      this.clearInlineStyles();
      this.mContainerEl.classList.add(VideoManager.CSS_FULL_SCREEN);
    }
    this.mPlayerViewSubscription.notify(PlayerViewMode.FullScreen);
  }

  public pip(): void {
    this.log("debug", "pip()");
    this.mCurrentViewMode = PlayerViewMode.PIP;
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.clearViewModeClasses();
      this.clearInlineStyles();
      this.mContainerEl.classList.add(VideoManager.CSS_PIP);
    }
    this.mPlayerViewSubscription.notify(PlayerViewMode.PIP);
  }

  public pig(config: TPigConfig): void {
    this.log("debug", "pig()");
    this.mCurrentViewMode = PlayerViewMode.PIG;
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.clearViewModeClasses();
      this.mContainerEl.classList.add(VideoManager.CSS_PIG);
      this.applyInlineStyles(config);
    }
    this.mPlayerViewSubscription.notify(PlayerViewMode.PIG);
  }

  public atmospheric(): void {
    this.log("debug", "atmospheric()");
    this.mCurrentViewMode = PlayerViewMode.Atmospheric;
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.clearViewModeClasses();
      this.clearInlineStyles();
      this.mContainerEl.classList.add(VideoManager.CSS_ATMOSPHERIC);
    }
    this.mPlayerViewSubscription.notify(PlayerViewMode.Atmospheric);
  }

  public browserFullScreen(): void {
    this.log("debug", "browserFullScreen()");
    this.mCurrentViewMode = PlayerViewMode.BrowserFullScreen;
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.clearViewModeClasses();
      this.clearInlineStyles();
      this.mContainerEl.classList.add(VideoManager.CSS_BROWSER_FULLSCREEN);
    }
    this.mPlayerViewSubscription.notify(PlayerViewMode.BrowserFullScreen);
  }

  public hidden(): void {
    this.log("debug", "hidden()");
    this.mCurrentViewMode = PlayerViewMode.Hidden;
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.clearViewModeClasses();
      this.clearInlineStyles();
      this.mContainerEl.classList.add(VideoManager.CSS_HIDDEN);
    }
    this.mPlayerViewSubscription.notify(PlayerViewMode.Hidden);
  }

  public homeScreenVideoWindow(): void {
    this.log("debug", "homeScreenVideoWindow() - delegating to atmospheric()");
    this.atmospheric();
  }

  public getPlayerViewMode(): PlayerViewMode {
    return this.mCurrentViewMode;
  }

  // ── Subscriptions ──────────────────────────────────────────────────────────

  public getPlayerStateSubscription(): ISubscription<PlayerState> {
    return this.mPlayerStateSubscription;
  }

  public getPlayerViewSubscription(): ISubscription<PlayerViewMode> {
    return this.mPlayerViewSubscription;
  }

  public getPlayerErrorSubscription(): ISubscription<TPlayerError> {
    return this.mPlayerErrorSubscription;
  }

  public getFirstFrameSubscription(): ISubscription<TFirstFrameEvent> {
    return this.mFirstFrameSubscription;
  }

  public getInitiationSubscription(): ISubscription<void> {
    return this.mInitiationSubscription;
  }

  public getDrmConfiguredSubscription(): ISubscription<void> {
    return this.mDrmConfiguredSubscription;
  }

  public getDrmFailedSubscription(): ISubscription<void> {
    return this.mDrmFailedSubscription;
  }

  public getPlayer(): IPlayer {
    return this.mPlayer;
  }

  // ── Private Helpers ────────────────────────────────────────────────────────

  private onStateChanged(event: TStateChangedEvent): void {
    this.log(
      "debug",
      "onStateChanged() - " + event.previousState + " -> " + event.newState
    );
    this.mPlayerStateSubscription.notify(event.newState);
  }

  private onError(event: TPlayerEventMap[PlayerState.ERROR]): void {
    this.log("error", "onError()", event.error);
    this.mPlayerStateSubscription.notify(PlayerState.ERROR);
    this.mPlayerErrorSubscription.notify(event.error);
  }

  private onFirstFrame(event: TPlayerEventMap[PlayerState.FIRST_FRAME]): void {
    this.log("debug", "onFirstFrame()");
    this.mPlayerStateSubscription.notify(PlayerState.FIRST_FRAME);
    this.mFirstFrameSubscription.notify(event);
  }

  private onInitiation(_event: TPlayerEventMap[PlayerState.INITIATION]): void {
    this.log("debug", "onInitiation()");
    this.mPlayerStateSubscription.notify(PlayerState.INITIATION);
    this.mInitiationSubscription.notify();
  }

  private onDrmConfigured(
    _event: TPlayerEventMap[PlayerState.DRM_CONFIGURED]
  ): void {
    this.log("debug", "onDrmConfigured()");
    this.mPlayerStateSubscription.notify(PlayerState.DRM_CONFIGURED);
    this.mDrmConfiguredSubscription.notify();
  }

  private onDrmFailed(_event: TPlayerEventMap[PlayerState.DRM_FAILED]): void {
    this.log("debug", "onDrmFailed()");
    this.mPlayerStateSubscription.notify(PlayerState.DRM_FAILED);
    this.mDrmFailedSubscription.notify();
  }

  /**
   * Removes every view-mode class, including `hidden`. Hidden is one of the six
   * mutually exclusive view modes, so requesting any visible mode reveals the
   * container without the caller also having to call mount().
   */
  private clearViewModeClasses(): void {
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.mContainerEl.classList.remove(
        VideoManager.CSS_FULL_SCREEN,
        VideoManager.CSS_PIP,
        VideoManager.CSS_PIG,
        VideoManager.CSS_ATMOSPHERIC,
        VideoManager.CSS_BROWSER_FULLSCREEN,
        VideoManager.CSS_HIDDEN
      );
    }
  }

  private applyInlineStyles(config: {
    width: number;
    height: number;
    top: number;
    left: number;
  }): void {
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      const scaler = DimensionScaler.getInstance();
      this.mContainerEl.style.width = scaler.toVwString(config.width);
      this.mContainerEl.style.height = scaler.toVhString(config.height);
      this.mContainerEl.style.top = scaler.toVhString(config.top);
      this.mContainerEl.style.left = scaler.toVwString(config.left);
    }
  }

  private clearInlineStyles(): void {
    if (this.mContainerEl !== null && this.mContainerEl !== undefined) {
      this.mContainerEl.style.width = "";
      this.mContainerEl.style.height = "";
      this.mContainerEl.style.top = "";
      this.mContainerEl.style.left = "";
    }
  }

  private buildPlaybackUrlRequestHeaders(): Record<string, string> {
    this.mClientRequestId += 1;
    const headers: Record<string, string> = {
      [VideoManager.HEADER_PLAYBACK_SESSION_ID]: this.mPlaybackSessionId,
      [VideoManager.HEADER_CLIENT_REQUEST_ID]: String(this.mClientRequestId)
    };
    // TODO: gate on client config add_x_device_user_agent_header_for_media_requests once available.
    headers[VideoManager.HEADER_DEVICE_USER_AGENT] = navigator.userAgent;
    return headers;
  }

  private buildDrmConfig(drmInfo: IVpcDrmInfo): TPlayerDrmConfig {
    const drmConfig: TPlayerDrmConfig = {};

    if (
      drmInfo.drmType === DrmType.Widevine ||
      drmInfo.drmType === DrmType.MobiWidevine
    ) {
      drmConfig.drmType = [DrmType.Widevine];
      drmConfig.widevineConfig = {
        licenseUrl: drmInfo.drmServerUrl,
        licenseRequestHeaders: {
          [drmInfo.drmCustomHeader]: drmInfo.token,
          "Content-Type": "application/octet-stream"
        }
      };
    } else if (
      drmInfo.drmType === DrmType.FairPlay ||
      drmInfo.drmType === DrmType.MobiFairPlay
    ) {
      if (!drmInfo.fairplayCertificateUrl) {
        this.log(
          "warn",
          "buildDrmConfig() - fairplayCertificateUrl is not set; FairPlay playback may fail"
        );
      }
      drmConfig.drmType = [DrmType.FairPlay];
      drmConfig.fairplayConfig = {
        licenseUrl: drmInfo.drmServerUrl,
        certificateUrl: drmInfo.fairplayCertificateUrl || "",
        licenseRequestHeaders: {
          [drmInfo.drmCustomHeader]: drmInfo.token,
          "Content-Type": "application/octet-stream"
        }
      };
    }

    return drmConfig;
  }
}
